.. <template>
    <div class="modal fade show my-2" aria-labelledby="addNewCardTitle" style="display: block; background: #1c1d1c33">
        <div class="modal-dialog modal-lg modal-dialog-centered" ref="modalRef">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                          <h1 class="mb-0">Create Payment</h1>
                            <button
                                @click="onClickClose"
                                type="button"
                                class="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>

                <hr class="mt-0 mb-0">

                <div class="pb-2">
                  <div class="row mx-2 pt-2">

                    <div class="col-12 col-md-6 mb-1">
                      <label class="form-label" for="date"> <span class="required">*</span> Date </label>
                      <input
                          class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
                          type="date"
                          v-model="formData.date"
                      >
                    </div>

                    <div class="col-12 col-md-6 mb-1" v-if="headId">
                      <label class="form-label" for="paidFrom"> Paid From </label>
                      <input
                          class="form-control"
                          type="text"
                          :value="headId.name"
                          disabled
                      >
                    </div>

                    <div class="col-12 mb-1">
                      <label for="attachment" class="form-label fw-bold">Attachment</label>
                      <div class="attachment-container">
                        <BlobFileViewer :attachments="formData.attachments" />
                        <FileUpload
                            :is-drag-active="true"
                            button-text="Upload Documents"
                            v-model="formData.attachments"
                        />
                      </div>
                    </div>

                    <div class="col-12 mb-1">
                      <div class="d-flex align-items-center justify-content-between filter-label">
                        <label for="colFormLabel" class="form-label">Party: </label>
                      </div>
                      <AsyncSelect
                          placeholder="Select Party"
                          v-model="contactProfile"
                          :api-service="fetchContactProfiles"
                          :format-label="formatPatientLabel"
                      />
                    </div>

                    <div class="col-12 mt-1">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <h5 class="mb-0">Transaction Details</h5>
                        <button @click="addEntry" type="button" class="btn btn-sm btn-outline-success">
                          <i class="fa fa-plus"></i> Add
                        </button>
                      </div>

                      <!-- Desktop Table -->
                      <div v-if="!isMobile" class="table-responsive" style="overflow: visible;">
                        <table class="table table-bordered mb-0" style="table-layout: fixed;">
                          <thead>
                          <tr>
                            <th style="width: 55%"><span class="required">*</span> Account Head</th>
                            <th style="width: 30%"><span class="required">*</span> Amount</th>
                            <th style="width: 15%" class="text-center"></th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(entry, index) in entries" :key="index">
                            <td>
                              <AsyncSelect
                                  placeholder="Select Account Head"
                                  v-model="entry.account_head"
                                  :api-service="fetchAccountHeadsList"
                                  :additional-query="{ cash_bank_hide: 'yes', status: 'active' }"
                              />
                            </td>
                            <td>
                              <input class="form-control" type="number" v-model="entry.amount" placeholder="0.00">
                            </td>
                            <td class="text-center">
                              <div v-if="entries.length > 1" @click="removeEntry(index)" type="button" class="text-danger">
                                <i class="fa fa-trash"></i>
                              </div>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Mobile Card Layout -->
                      <div v-else>
                        <div v-for="(entry, index) in entries" :key="index" class="entry-card">
                          <div class="entry-card-header">
                              <span style="font-size: 0.8rem; font-weight: 600; color: #5e5873;">
                                Entry #{{ index + 1 }}
                              </span>
                            <div
                                v-if="entries.length > 1"
                                @click="removeEntry(index)"
                                type="button"
                                class="text-danger"
                            >
                              <i class="fa fa-trash"></i>
                            </div>
                          </div>
                          <div class="mb-2">
                            <div class="entry-card-label"><span class="required">*</span> Account Head</div>
                            <AsyncSelect
                                placeholder="Select Account Head"
                                v-model="entry.account_head"
                                :api-service="fetchAccountHeadsList"
                                :additional-query="{ cash_bank_hide: 'yes', status: 'active' }"
                            />
                          </div>
                          <div>
                            <div class="entry-card-label"><span class="required">*</span> Amount</div>
                            <input class="form-control" type="number" v-model="entry.amount" placeholder="0.00">
                          </div>
                        </div>
                      </div>

                    </div>

                    <div class="col-12 mb-1 mt-1">
                      <label class="form-label" for="note"> Note </label>
                      <textarea
                          class="form-control"
                          v-model="formData.note"
                          rows="2"
                          placeholder="Note"
                      ></textarea>
                    </div>

                    <div class="col-12 text-center mb-2 mt-2">
                      <button :disabled="loading" @click="submit" type="button" class="btn btn-primary full-width me-1 waves-effect waves-float waves-light">
                        <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
            </div>
        </div>
        <Loader v-if="loader"/>
    </div>
</template>

<script setup>
import Loader from "@/components/atom/LoaderComponent";
import FileUpload from "@/components/atom/FileUpload";
import {inject, onMounted, onUnmounted, ref} from "vue";
import { useRoute } from 'vue-router';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from "@/services/modules/contact";
import handleJournal from '@/services/modules/journal';
import handlePayment from '@/services/modules/payment';
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import BlobFileViewer from "@/components/atom/BlobFileViewer";

const props = defineProps({
    headId: {
      type: Object,
    },
});

const emit = defineEmits(['submit', 'close']);
const $route = useRoute();
const loading = ref(false);
const loader = ref(false);
const contactProfile = ref(null);
const entries = ref([{ account_head: null, amount: null }]);
const { formatPatientLabel } = useAsyncDropdownHelper();

const formData = ref({
  date: new Date().toISOString().split('T')[0],
  note: '',
  attachments: []
});

const showError = inject('showError');
const showSuccess = inject('showSuccess');

const { fetchContactProfiles } = handleContact();
const { fetchAccountHeadsList } = handleJournal();
const { createPayment } = handlePayment();

const addEntry = () => {
    entries.value.push({ account_head: null, amount: null });
};

const removeEntry = (index) => {
    if (entries.value.length > 1) {
        entries.value.splice(index, 1);
    }
};

const submit = async () => {
    if (!formData.value.date) {
      showError("Please select a date");
      return;
    }

    if (!props.headId) {
      showError("Paid From account is missing");
      return;
    }

    let isValid = true;
    let totalAmount = 0;

    for (let i = 0; i < entries.value.length; i++) {
        const entry = entries.value[i];
        if (!entry.account_head) {
            isValid = false;
        }
        if (!entry.amount) {
            isValid = false;
        }
        if (entry.amount) {
            totalAmount += parseFloat(entry.amount);
        }
    }

    if (!isValid) {
        showError("Please fill all Account Head and Amount fields");
        return;
    }

    if (totalAmount <= 0) {
        showError("Total amount must be greater than zero");
        return;
    }

    loader.value = true;
    loading.value = true;
    
    let data = new FormData();
    data.append('company_id', $route.params.companyId);
    data.append('date', formData.value.date);
    data.append('note', formData.value.note);
    data.append('voucher_type', 'payment_voucher');
    data.append('mop_references', 'mop');
    data.append('txn_type', 'general_payment');

    let generalLedgers = [];
    
    let contactId = null;
    if (contactProfile.value) {
      contactId = contactProfile.value.id;
    }

    let note = "";
    if (formData.value.note) {
      note = formData.value.note;
    }

    for (let i = 0; i < entries.value.length; i++) {
        let entry = entries.value[i];
        if (entry.account_head) {
            generalLedgers.push({
              account_head_id: entry.account_head.id,
              debit: entry.amount,
              credit: null,
              note: note,
              contact_profile_id: contactId,
              business_id: null,
              tag_register_id: null,
              tax_rate: null,
              taxable_amount: null,
              mop_account_head_id: props.headId.id
            });
        }
    }

    generalLedgers.push({
      account_head_id: props.headId.id,
      debit: null,
      credit: totalAmount,
      note: note,
      contact_profile_id: contactId,
      business_id: null,
      tag_register_id: null,
      tax_rate: null,
      taxable_amount: null,
      project_id: null,
      donor_id: null,
      cost_centre_id: null,
      mop_account_head_id: null
    });
    
    data.append('general_ledgers', JSON.stringify(generalLedgers));
    
    if (formData.value.attachments && formData.value.attachments.length > 0) {
     for (let i = 0; i < formData.value.attachments.length; i++) {
       data.append(`attachments[${i}]`, formData.value.attachments[i]);
     }
    }

    try {
      const res = await createPayment(data);
      if (!res.status) {
        return showError(res.message)
      }
      showSuccess(res.message);
      emit('submit');
      onClickClose();
    } catch (err) {
      showError("Something went wrong. Check your connectivity!!");
    } finally {
      loading.value = false;
      loader.value = false;
    }
};

const isMobile = ref(false);
const handleResize = () => { isMobile.value = window.innerWidth <= 576; };
onMounted(() => {
  isMobile.value = window.innerWidth <= 576;
  window.addEventListener('resize', handleResize);
});
onUnmounted(() => window.removeEventListener('resize', handleResize));

const onClickClose = () => {
    emit('close');
};
</script>

<style scoped>
.required {
  color: red;
}
.full-width {
  width: 100%;
}
.attachment-container {
  background: #f8f9fa;
  border: 2px dashed #d8dbe0;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;
}
.attachment-container:hover {
  border-color: #7367f0;
  background: #f0f0ff;
}

@media (max-width: 576px) {
  .modal-dialog {
    margin: 0;
    max-width: 100%;
    min-height: 100vh;
    align-items: flex-end;
  }
  .modal-content {
    border-radius: 16px 16px 0 0;
    max-height: 95vh;
    overflow-y: auto;
  }
}

/* Mobile entry card styles */
.entry-card {
  background: #f8f9fa;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.25rem;
  position: relative;
}
.entry-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.entry-card-label {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}
</style>
